import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { PrintIcon, PaperclipIcon } from "../../../../Icons";
import {
  MODAL_PANEL,
  MODAL_TITLE,
  BTN_SECONDARY,
  BTN_PRIMARY,
} from "../../../../ui/pageChrome";

/**
 * Ask whether bulk print should interleave attachments after each form.
 * Resolves via onChoose: "with" | "without" | null (cancel).
 */
const ClinicalFormBulkPrintPrompt = ({
  open,
  formCount = 0,
  onChoose,
}) => {
  const primaryRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onChoose?.(null);
    };
    document.addEventListener("keydown", onKey);
    const t = window.setTimeout(() => primaryRef.current?.focus(), 30);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
    };
  }, [open, onChoose]);

  if (!open) return null;

  const countLabel =
    formCount === 1 ? "1 selected form" : `${formCount} selected forms`;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bulk-print-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/40"
        aria-label="Cancel"
        onClick={() => onChoose?.(null)}
      />
      <div className={`relative w-full max-w-md ${MODAL_PANEL}`}>
        <div className="flex items-start gap-3 px-5 pt-5 pb-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100 shrink-0">
            <PrintIcon size={18} />
          </span>
          <div className="min-w-0 pt-0.5">
            <h2 id="bulk-print-title" className={MODAL_TITLE}>
              Print selected forms
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Include attachments with each of the {countLabel}?
            </p>
            <p className="mt-2 text-[12px] text-slate-500 leading-relaxed">
              If yes, each form is shown first, then its attachments, then the
              next form — ready to print or save as PDF.
            </p>
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-5 py-4 border-t border-slate-100 bg-slate-50/60 rounded-b-xl">
          <button
            type="button"
            className={BTN_SECONDARY}
            onClick={() => onChoose?.(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className={BTN_SECONDARY}
            onClick={() => onChoose?.("without")}
          >
            Forms only
          </button>
          <button
            ref={primaryRef}
            type="button"
            className={`${BTN_PRIMARY} inline-flex items-center justify-center gap-1.5`}
            onClick={() => onChoose?.("with")}
          >
            <PaperclipIcon size={13} />
            With attachments
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

ClinicalFormBulkPrintPrompt.propTypes = {
  open: PropTypes.bool,
  formCount: PropTypes.number,
  onChoose: PropTypes.func,
};

export default ClinicalFormBulkPrintPrompt;
